import './App.css';
import { useState, useEffect, useCallback } from 'react';
import { supabase } from './lib/supabase';
import Login from './components/Login';
import Home from './components/Home';
import AddInquiry from './components/AddInquiry';
import ViewBookings from './components/ViewBookings';
import DraftInquiries from './components/DraftInquiries';
import ReviewDraft from './components/ReviewDraft';
import Settings from './components/Settings';
import Partners from './components/Partners';
import Operations from './components/Operations';
import DailyTracker from './components/DailyTracker';
import SeatingSheet from './components/SeatingSheet';
import Reports from './components/Reports';
import RangeReports from './components/RangeReports';
import CollectionsReport from './components/CollectionsReport';
import PartnerLedger from './components/PartnerLedger';
import TallyExport from './components/TallyExport';
import SecuritySettings from './components/SecuritySettings';
import CruiseSettings from './components/CruiseSettings';
import WebsiteContent from './components/WebsiteContent';
import TicketPreview from './components/TicketPreview';
import Maintenance from './components/Maintenance';
import InvoiceSettings from './components/InvoiceSettings';
import ImportData from './components/ImportData';
import NotificationsBell from './components/NotificationsBell';
import CapacityAlertsIcon from './components/CapacityAlertsIcon';
import CheckIn from './components/CheckIn';
import Enquiries from './components/Enquiries';
import IncompleteBookings from './components/IncompleteBookings';
import Payroll from './components/Payroll';
import Events from './components/Events';
import EventForm from './components/EventForm';
import SideNav from './components/SideNav';
import { Menu } from 'lucide-react';

// ─── URL routing helpers ──────────────────────────────────────────────────────
const SCREEN_TO_PATH = {
  'checkin': '/checkin',
  'home':            '/',
  'view-bookings':   '/bookings',
  'daily-tracker':   '/tracker',
  'seating-sheet':   '/seating',
  'reports':           '/reports',
  'range-reports':     '/range-reports',
  'invoice-export':    '/invoice-export',
  'settings':          '/settings',
  'cruise-settings':   '/settings/cruises',
  'website-content':   '/settings/website',
  'ticket-preview':    '/settings/ticket',
  'partners':          '/settings/partners',
  'maintenance':       '/settings/maintenance',
  'invoice-settings':  '/settings/invoice',
  'security-settings': '/settings/security',
  'draft-inquiries': '/drafts',
  'add-inquiry':     '/add',
  'operations':      '/operations',
  'enquiries':       '/enquiries',
  'incomplete-bookings': '/incomplete',
  'import-data':     '/operations/import',
  'events':          '/events',
  'event-form':      '/events/form',
};
const PATH_TO_SCREEN = Object.fromEntries(
  Object.entries(SCREEN_TO_PATH).map(([s, p]) => [p, s])
);
function buildUrl(screen, date, cruise) {
  const path = SCREEN_TO_PATH[screen];
  if (!path) return null;
  const params = new URLSearchParams();
  if (date)   params.set('date', date);
  if (cruise) params.set('cruise', cruise);
  const qs = params.toString();
  const fragment = qs ? `${path}?${qs}` : path;
  return `#${fragment}`;
}
function parseUrl() {
  // Hash-based routing: #/tracker?date=18-05-2026&cruise=Sunset+Cruise
  // The hash is never sent to the server, so page refreshes always load index.html
  const hash = window.location.hash.slice(1) || '/';  // strip leading #
  const [pathPart, queryPart] = hash.split('?');
  const params = new URLSearchParams(queryPart || '');
  return {
    screen: PATH_TO_SCREEN[pathPart] || 'home',
    date:   params.get('date')   || '',
    cruise: params.get('cruise') || '',
  };
}

export default function App() {
  const [session, setSession]         = useState(null);   // Supabase Auth session (null = signed out)
  const [checkingAuth, setCheckingAuth] = useState(true); // true while the initial getSession() is in flight
  const [screen, setScreen]           = useState(() => parseUrl().screen);
  const [activeDate, setActiveDate]   = useState(() => parseUrl().date);
  const [activeCruise, setActiveCruise] = useState(() => parseUrl().cruise);
  const [editData, setEditData]       = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false); // mobile side-nav drawer

  // Browser back/forward
  useEffect(() => {
    const onPop = () => {
      const { screen: s, date: d, cruise: c } = parseUrl();
      setScreen(s);
      setActiveDate(d);
      setActiveCruise(c);
      setEditData(null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Supabase Auth gate. Read the existing session once on load (with a loading
  // state so we never flash the login screen for someone already signed in),
  // then keep the screen in sync via onAuthStateChange. Uses the single shared
  // supabase client, so the session is the same one every query already uses.
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setCheckingAuth(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });
    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut(); // onAuthStateChange clears the session → login screen
    setScreen('home');
    setActiveDate('');
    setActiveCruise('');
    setEditData(null);
    window.history.replaceState({}, '', '/');
  };

  // navigate(screen, date?, editItem?)
  const navigate = useCallback((dest, date, data) => {
    setScreen(dest);
    if (date !== undefined) setActiveDate(date || '');
    if (data?.cruise_type) setActiveCruise(data.cruise_type);
    setEditData(data || null);
    const url = buildUrl(dest, date || '', data?.cruise_type || '');
    if (url) window.history.pushState({ screen: dest }, '', url);
  }, []);

  // Called by DailyTracker/SeatingSheet when user navigates dates or changes cruise internally
  const handleStateChange = useCallback((ddmmyyyy, cruise) => {
    setActiveDate(ddmmyyyy);
    setActiveCruise(cruise);
    const url = buildUrl(screen, ddmmyyyy, cruise);
    if (url) window.history.replaceState({ screen }, '', url);
  }, [screen]);

  // While the initial session check runs, show a minimal centered wordmark —
  // do not flash the login screen for an already-signed-in user.
  if (checkingAuth) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <h1 className="app-wordmark" style={{ fontSize: '1.5rem', opacity: 0.55 }}>Alfresco Grand</h1>
      </div>
    );
  }

  // No session → only the login screen renders; no routes/screens are reachable.
  if (!session) return <Login />;

  // Check-in route — gated by the staff Supabase session like every other screen.
  if (screen === 'checkin') return <CheckIn />;

  const renderScreen = () => {
    switch (screen) {
      case 'add-inquiry':
        return <AddInquiry date={activeDate} onNavigate={navigate} />;
      case 'add-inquiry-prefill':
        return <AddInquiry date={activeDate} onNavigate={navigate} prefillData={editData} />;
      case 'edit-inquiry':
        return <AddInquiry date={activeDate} onNavigate={navigate} editData={editData} />;
      case 'view-bookings':
        return <ViewBookings date={activeDate} onNavigate={navigate} />;
      case 'draft-inquiries':
        return <DraftInquiries onNavigate={navigate} />;
      case 'review-draft':
        return <ReviewDraft draftData={editData} onNavigate={navigate} />;
      case 'settings':
        return <Settings onNavigate={navigate} />;
      case 'cruise-settings':
        return <CruiseSettings onNavigate={navigate} />;
      case 'website-content':
        return <WebsiteContent onNavigate={navigate} />;
      case 'ticket-preview':
        return <TicketPreview onNavigate={navigate} />;
      case 'maintenance':
        return <Maintenance onNavigate={navigate} />;
      case 'invoice-settings':
        return <InvoiceSettings onNavigate={navigate} />;
      case 'partners':
        return <Partners onNavigate={navigate} />;
      case 'operations':
        return <Operations onNavigate={navigate} />;
      case 'enquiries':
        return <Enquiries onNavigate={navigate} />;
      case 'incomplete-bookings':
        return <IncompleteBookings onNavigate={navigate} />;
      case 'payroll':
        return <Payroll onNavigate={navigate} />;
      case 'events':
        return <Events onNavigate={navigate} />;
      case 'event-form':
        return <EventForm key={editData?.id || 'new'} editData={editData} onNavigate={navigate} />;
      case 'import-data':
        return <ImportData onNavigate={navigate} />;
      case 'reports':
        return <Reports onNavigate={navigate} initialDate={editData?._from_tracker ? activeDate : null} />;
      case 'range-reports':
        return <RangeReports onNavigate={navigate} />;
      case 'collections-report':
        return <CollectionsReport onNavigate={navigate} />;
      case 'partner-ledger':
        return <PartnerLedger onNavigate={navigate} />;
      case 'invoice-export':
        return <TallyExport onNavigate={navigate} />;
      case 'security-settings':
        return <SecuritySettings onNavigate={navigate} />;
      case 'daily-tracker':
        return <DailyTracker onNavigate={navigate} initialDate={activeDate} initialCruise={editData?.cruise_type || activeCruise} onStateChange={handleStateChange} />;
      case 'seating-sheet':
        return <SeatingSheet date={activeDate} initialCruise={editData?.cruise_type || activeCruise} highlightBookingId={editData?.highlight_booking_id} onNavigate={navigate} onStateChange={handleStateChange} />;
      default:
        return <Home onNavigate={navigate} />;
    }
  };

  const fullWidthScreens = ['daily-tracker', 'seating-sheet', 'home'];

  return (
    <div className="app-shell">
      <SideNav
        screen={screen}
        onNavigate={(k) => { navigate(k); setMobileNavOpen(false); }}
        onLogout={handleLogout}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <div className="app-main">
        <div className={fullWidthScreens.includes(screen) ? 'app-wrapper-full' : 'app-wrapper'}>
          <header className="app-header">
            <button
              type="button"
              className="app-nav-toggle"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation"
            >
              <Menu size={22} strokeWidth={2} aria-hidden="true" />
            </button>
            <div
              className="app-brand"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('home')}
              title="Go to home"
            >
              <h1 className="app-wordmark">Alfresco Grand</h1>
              <span className="app-brand-tag">Bookings</span>
            </div>
            <div className="app-header-actions">
              <CapacityAlertsIcon onNavigate={navigate} />
              <NotificationsBell onNavigate={navigate} />
            </div>
          </header>

          {renderScreen()}
        </div>
      </div>
    </div>
  );
}
